import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import WithStripe from 'components/common/WithStripe';
import get from 'lodash/get';
import sumBy from 'lodash/sumBy';
import { format, parse } from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';
import { store } from 'react-notifications-component';
import { batch } from 'react-redux';
import {
  cateringOrder as fetchCateringOrder,
  cateringOrderUpdate,
  cateringOrderFinalize,
  clearCateringOrder,
  addLastMinuteItemPayment,
} from 'redux/catering/actions';
import { resetCheckoutData } from 'redux/restaurants/actions';
import { removeUserCart } from 'redux/userCart/actions';
import { showSignInModal } from 'redux/actions/modalActions';
import { signoutRequest } from 'redux/actions/authActions';
import { validate } from 'api/authApi';
import { getDeliveryInfo } from 'utils/address.util';
import {
  useDeliveryFee,
  resolveDeliveryFeeWithFallback,
} from 'hooks/useDeliveryFee';
import {
  getPresetSubTotalPrice,
  buildPaymentIntentPresetItems,
  getPresetItemQuantity,
} from 'utils/preset.util';
import { priceDecimalFormatter } from 'formatter/preset.formatter';
import {
  LOADING_CATERINGORDER_FALSE,
  LOADING_CATERINGORDER_TRUE,
} from 'redux/catering/actionTypes';
import { SERVING_STYLE } from 'consts/checkout';
import { REQUIRED_SIGNIN_FOR_PAYMENT } from 'consts/form';
import { CheckoutView } from 'panda-ui/components/CheckoutView';
import DeliveryInfoFormInline from 'components/common/DeliveryInfoForm/DeliveryInfoFormInline';
import StripeFormModal from './StripeFormModal';
import EftFormModal from './EftFormModal';
import './style.scss';

const buildCart = (form, checkoutData, preset, receivingMethod, deliveryFee) => {
  const {
    firstName,
    lastName,
    companyName,
    phoneNumber,
    emailAddress,
    servingStyle,
  } = form;
  const cart = {
    presetId: preset.id,
    presetName: preset.name,
    numCutlery: form.cutleryNeeded ? parseInt(form.cutleryCount, 10) : 0,
    firstName,
    lastName,
    companyName,
    phoneNumber,
    email: emailAddress,
    emailAddress,
    servingStyle,
    presetItems: buildPaymentIntentPresetItems(checkoutData),
    receivingMethod,
  };

  const timezone =
    get(preset, 'presetLocation.cityTimeZoneName') || 'America/Toronto';

  const dataTime =
    form && form.date && form.time
      ? (() => {
          const localString = `${format(form.date, 'yyyy-MM-dd')} ${format(
            form.time,
            'HH:mm',
          )}`;
          return zonedTimeToUtc(
            parse(localString, 'yyyy-MM-dd HH:mm', new Date()),
            timezone,
          );
        })()
      : '';

  if (receivingMethod === 'delivery') {
    cart.deliveryAddress = form ? form.address : '';
    cart.deliverySuiteAddress = form ? form.address2 : '';
    cart.deliveryDate = dataTime;
    cart.deliveryTime = dataTime;
    cart.deliveryInstruction = form ? form.notes : '';
    cart.deliveryFee = deliveryFee;
  } else {
    cart.pickupDate = dataTime;
    cart.pickupTime = dataTime;
    cart.pickupInstruction = form ? form.notes : '';
  }
  return cart;
};

const PandaCheckout = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { state } = useLocation();
  const user = useSelector(s => s.user?.user);
  const receivingMethod = useSelector(s => s.receivingMethod);
  const cateringOrder = useSelector(s => s.caterings?.cateringOrder);
  const loading = useSelector(s => s.caterings?.loading);

  const { preset, checkoutData, restaurantAddress } = state || {};
  const deliveryPriceCents =
    get(preset, 'deliveryPriceCents') ||
    get(preset, 'chefs[0].deliveryFeeCents') ||
    0;

  // Real signed-in customer + real delivery info the user already picked
  // while browsing - CheckoutView must not fall back to placeholder data
  // ("Kim Yuan", a stale hardcoded date, etc.) once these are available,
  // since that placeholder data was silently getting submitted as the
  // actual order when a user trusted the prefilled form.
  const initialCustomer = useMemo(
    () => ({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      companyName: user?.companyName || '',
      phone: user?.phone || '',
      email: user?.email || '',
    }),
    [user],
  );
  // Bumped whenever the delivery info bar at the top of this page saves a
  // change, so initialDelivery is recomputed and CheckoutView (which only
  // reads its initial address/date/time once, on mount) gets a fresh key
  // and re-mounts with the new values instead of silently keeping the old
  // ones in its own local form state.
  const [deliveryBarVersion, setDeliveryBarVersion] = useState(0);
  const initialDelivery = useMemo(() => {
    const deliveryInfo = getDeliveryInfo();
    // CheckoutView keeps date/time as plain strings (yyyy-MM-dd / h:mm a),
    // matching what's already stored in jb-delivery-info - no need to round
    // -trip through Date objects here.
    return {
      address: deliveryInfo?.address || '',
      date: deliveryInfo?.delivery_date || '',
      time: deliveryInfo?.delivery_time || '',
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryBarVersion]);

  const [cutleryCount, setCutleryCount] = useState(0);
  const [saveCard, setSaveCard] = useState(false);
  const [currentModel, setCurrentModel] = useState(null);
  const [stripeErrorMessage, setStripeErrorMessage] = useState('');
  const [discountAmount, setDiscountAmount] = useState(0);

  // The real delivery fee is the live distance-based lookup - it only
  // falls back to the chef's flat deliveryPriceCents when that lookup
  // can't be completed, so the estimate is never blocked on a broken
  // address/lookup and always shows a real number.
  const { deliveryFee } = useDeliveryFee(
    get(preset, 'chefs[0].id'),
    receivingMethod === 'delivery' ? initialDelivery?.address || '' : '',
    deliveryPriceCents,
  );

  const subTotal =
    getPresetSubTotalPrice(checkoutData) + 50 * cutleryCount;
  const percentValue = priceDecimalFormatter(subTotal * preset?.taxRate) * 100;
  const totalPrice =
    receivingMethod === 'delivery'
      ? (subTotal + deliveryFee) * (1 + preset?.taxRate)
      : subTotal * (1 + preset?.taxRate);

  const isSignedIn = user && user.confirmed;

  const taxRate = preset?.taxRate || 0;

  const cartItems = useMemo(
    () =>
      (checkoutData || []).map(item => ({
        id: item.id,
        menuItemId: item.menuId,
        name: item.name,
        quantity: item.quantity || 0,
        price: (item.priceCents || 0) / 100,
        image: '/panda_avatar.jpg',
        instructions: item.specialInstruction || '',
        options: [],
      })),
    [checkoutData],
  );

  const getPaymentKey = async (form, eft = false) => {
    try {
      dispatch({ type: LOADING_CATERINGORDER_TRUE });
      // Re-resolve fresh for the exact address being submitted (with the
      // same real-distance-first, static-fallback behavior as the live
      // estimate) rather than trusting the render-scoped deliveryFee,
      // which could still reflect a previous address/lookup.
      const currentDeliveryFee =
        receivingMethod === 'delivery'
          ? (
              await resolveDeliveryFeeWithFallback(
                get(preset, 'chefs[0].id'),
                form.address,
                deliveryPriceCents,
              )
            ).deliveryFee
          : 0;
      const cart = buildCart(
        form,
        checkoutData,
        preset,
        receivingMethod,
        currentDeliveryFee,
      );
      const orderResult = cateringOrder
        ? await dispatch(
            cateringOrderUpdate(cart, cateringOrder?.purchaseId, eft),
          )
        : await dispatch(fetchCateringOrder(cart, eft));

      // The backend independently recomputes the order total from the cart
      // it received - if that ever disagrees with what we're about to show
      // the customer on the payment modal, refuse to open it rather than
      // let someone pay an amount that doesn't match the displayed total
      // (this is what let a $1349.24 order get charged as $38.06 - the
      // modal only ever showed the frontend's own number, never the
      // backend's authoritative one).
      //
      // Computed from currentDeliveryFee/form.cutleryCount directly rather
      // than the render-scoped subTotal/totalPrice/cutleryCount state -
      // those come from state set moments ago in this same function call
      // and haven't re-rendered yet, so they'd still hold the previous
      // (stale) values here and falsely trip this check on every order.
      const cutleryUnits = form.cutleryNeeded
        ? parseInt(form.cutleryCount, 10) || 0
        : 0;
      const expectedSubTotal =
        getPresetSubTotalPrice(checkoutData) + 50 * cutleryUnits;
      // Mirrors the backend's own computation order (round the tax amount
      // on its own, then add it to the pre-tax total) instead of
      // multiplying by (1 + taxRate) in one shot and rounding the result.
      // Those two orderings are mathematically equivalent but not
      // floating-point-identical - e.g. 25050 * 1.13 evaluates to
      // 28306.499999999996 in JS (just under the exact .5), rounding down
      // to 28306, while round(25050 * 0.13) = round(3256.5) = 3257 rounds
      // up, then 25050 + 3257 = 28307 - a 1-cent gap that isn't a real
      // mismatch, just two valid ways of rounding the same number landing
      // on different sides of a .5 boundary.
      const expectedSubTotalPlusDelivery =
        receivingMethod === 'delivery'
          ? expectedSubTotal + currentDeliveryFee
          : expectedSubTotal;
      const expectedTaxAmount = Math.round(
        expectedSubTotalPlusDelivery * taxRate,
      );
      const frontendTotalCents =
        expectedSubTotalPlusDelivery + expectedTaxAmount;
      const backendTotalCents = orderResult?.priceInfo?.totalAmount;
      // A few cents of tolerance absorbs any remaining rounding-order
      // differences between the two systems (this check exists to catch
      // gross mismatches like the original $1349 vs $38 incident, not to
      // enforce penny-perfect equality with floating-point money math).
      if (
        typeof backendTotalCents === 'number' &&
        Math.abs(backendTotalCents - frontendTotalCents) > 5
      ) {
        await dispatch(clearCateringOrder());
        store.addNotification({
          title: 'Checkout Error',
          message:
            'We could not confirm the order total with the server. Please refresh the page and try again before paying.',
          type: 'danger',
          insert: 'top',
          container: 'top-right',
          animationIn: ['animate__animated', 'animate__fadeIn'],
          animationOut: ['animate__animated', 'animate__fadeOut'],
          dismiss: { duration: 8000, onScreen: true },
        });
        return false;
      }
      return true;
    } catch (e) {
      dispatch({ type: LOADING_CATERINGORDER_FALSE });
      // Clear out any order from a previous attempt so a failed retry can
      // never leave the payment modal pointing at a stale purchaseId/saved
      // cards that don't belong to this checkout attempt.
      await dispatch(clearCateringOrder());
      const message =
        e?.message ||
        'Unable to create order. Please check your details and try again.';
      store.addNotification({
        title: 'Checkout Error',
        message,
        type: 'danger',
        insert: 'top',
        container: 'top-right',
        animationIn: ['animate__animated', 'animate__fadeIn'],
        animationOut: ['animate__animated', 'animate__fadeOut'],
        dismiss: { duration: 6000, onScreen: true },
      });
      return false;
    } finally {
      dispatch({ type: LOADING_CATERINGORDER_FALSE });
    }
  };

  const onResetCart = () => {
    batch(() => {
      dispatch(resetCheckoutData());
      dispatch(removeUserCart(preset?.id));
    });
  };

  const orderHandler = async result => {
    setCurrentModel(null);
    dispatch({ type: LOADING_CATERINGORDER_TRUE });
    if (result && result.error) {
      dispatch({ type: LOADING_CATERINGORDER_FALSE });
      setStripeErrorMessage(result.error.message);
    } else {
      try {
        dispatch({ type: LOADING_CATERINGORDER_TRUE });
        await dispatch(cateringOrderFinalize(
          result,
          cateringOrder?.purchaseId,
          saveCard,
        ));
        await dispatch(clearCateringOrder());
        onResetCart();
        history.push('/checkout-completed');
      } catch (e) {
        dispatch({ type: LOADING_CATERINGORDER_FALSE });
        store.addNotification({
          message:
            'Something went wrong in processing your request. Please verify the details and try again.',
          type: 'danger',
          insert: 'top',
          container: 'top-right',
          animationIn: ['animated', 'bounceIn'],
          animationOut: ['animated', 'fadeOut'],
          dismiss: { duration: 4000 },
        });
        console.error(e);
      }
    }
  };

  const onPlaceOrder = async (
    customer,
    delivery,
    paymentType = 'card',
  ) => {
    if (!isSignedIn) {
      dispatch(showSignInModal(REQUIRED_SIGNIN_FOR_PAYMENT));
      return;
    }

    const form = {
      firstName: customer.firstName,
      lastName: customer.lastName,
      companyName: customer.companyName,
      phoneNumber: customer.phone,
      emailAddress: customer.email,
      address: delivery.address,
      address2: delivery.suite || '',
      date: delivery.date
        ? parse(delivery.date, 'yyyy-MM-dd', new Date())
        : new Date(),
      time: delivery.time
        ? parse(delivery.time, 'h:mm a', new Date())
        : new Date(),
      notes: delivery.instructions,
      servingStyle:
        delivery.packagingPreference === 'family'
          ? SERVING_STYLE.FAMILY
          : SERVING_STYLE.INDIVIDUAL,
      cutleryNeeded: delivery.needsCutlery,
      cutleryCount: delivery.cutleryCount || 0,
    };

    setCutleryCount(parseInt(delivery.cutleryCount, 10) || 0);

    const showEft = paymentType === 'eft';
    // Only open the payment modal once the order was actually created on
    // the backend - opening it eagerly let the modal show a stale/previous
    // cateringOrder's saved cards when this attempt failed, letting people
    // "pay" against an order that was never created for their current cart.
    const success = await getPaymentKey(form, showEft);
    if (success) {
      setCurrentModel(showEft ? 'eft' : 'card');
    }
  };

  const onApplyPromo = code => {
    if (!code || !code.trim()) {
      setDiscountAmount(0);
      return false;
    }
    const discount = 5;
    setDiscountAmount(discount);
    return true;
  };

  const totalQuantity = useMemo(() => {
    if (!checkoutData || checkoutData.length === 0) return 0;
    return sumBy(checkoutData, item => getPresetItemQuantity(item));
  }, [checkoutData]);

  return (
    <main>
      <DeliveryInfoFormInline
        isAbleToChangeReceivingMethod
        onSubmit={() => setDeliveryBarVersion(v => v + 1)}
      />
      {loading && (
        <div className="p-10 text-center flex flex-col items-center justify-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-gray-200 border-t-[#E53935] mb-3" />
          <p className="text-gray-600 text-sm">Processing your order...</p>
        </div>
      )}
      <section className="checkout-banner relative overflow-hidden bg-gradient-to-br from-[#7B0D0D] via-[#991B1B] to-[#B91C1C] text-white shadow-2xl border-b border-red-700/60">
        <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
          <img
            src="/hero_banner.jpg"
            alt="Delicious food spread"
            className="w-full h-full object-cover object-center opacity-50 scale-105"
            referrerPolicy="no-referrer"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#700B0B] via-[#8B0E0E]/85 to-[#991B1B]/40 md:bg-gradient-to-r md:from-[#700B0B] md:via-[#8B0E0E]/85 md:to-red-900/30" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto p-5 text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-extrabold tracking-tight text-white leading-tight">
            Complete your order
          </h1>
          <p className="mt-4 text-red-50 text-base sm:text-lg max-w-2xl mx-auto">
            Review your catering details and we&apos;ll deliver fresh, delicious
            meals to your office.
          </p>
        </div>
      </section>
      <div className="container-fluid checkout py-5 mb-5">
        {/* deliveryFee is cents (same static per-chef fee as the menu
            page) - it's added directly to the raw priceCents-based
            subTotal/totalPrice above. CheckoutView works entirely in
            dollars, so it's converted only at this boundary. */}
        <CheckoutView
          key={deliveryBarVersion}
          cartItems={cartItems}
          onBack={() => history.push('/browse-restaurants')}
          onPlaceOrder={onPlaceOrder}
          appliedPromo=""
          onApplyPromo={onApplyPromo}
          discountAmount={discountAmount}
          initialCustomer={initialCustomer}
          initialDelivery={initialDelivery}
          deliveryFee={deliveryFee / 100}
          taxRate={taxRate}
        />
        {stripeErrorMessage && (
          <div className="alert alert-danger mt-3 text-center">
            {stripeErrorMessage}
          </div>
        )}
        <StripeFormModal
          show={currentModel === 'card'}
          onClose={() => setCurrentModel(null)}
          orderHandler={orderHandler}
          loadingHandler={() => dispatch({ type: LOADING_CATERINGORDER_TRUE })}
          clearCateringOrder={() => dispatch(clearCateringOrder())}
          cateringOrder={cateringOrder}
          totalPrice={totalPrice}
          setSaveCard={setSaveCard}
          loader={loading}
        />
        <EftFormModal
          show={currentModel === 'eft'}
          onClose={() => setCurrentModel(null)}
          orderHandler={orderHandler}
          loadingHandler={() => dispatch({ type: LOADING_CATERINGORDER_TRUE })}
          clearCateringOrder={() => dispatch(clearCateringOrder())}
          cateringOrder={cateringOrder}
          totalPrice={totalPrice}
          setSaveCard={setSaveCard}
        />
      </div>
    </main>
  );
};

// Stripe.js is only loaded once this page actually mounts, not globally on
// app load - see src/index.js and components/common/WithStripe for why.
const PandaCheckoutWithStripe = props => (
  <WithStripe>
    <PandaCheckout {...props} />
  </WithStripe>
);

export default PandaCheckoutWithStripe;
